
import { useParams, useNavigate } from "react-router-dom";
import { SEO } from "@/components/common/SEO";
import { Header } from "@/components/learn-more/Header";
import { Footer } from "@/components/learn-more/Footer";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { BlogSidebar } from "@/components/blog/BlogSidebar";
import { blogPosts } from "@/data/blogPosts";
import { RelatedPosts } from "@/components/blog/RelatedPosts";
import { useBlogImage } from "@/hooks/useBlogImage";
import { Share, ArrowLeft, Clock, Briefcase, DollarSign, Megaphone, Code, Palette, FileText } from "lucide-react";
import { generatePageStructuredData } from "@/utils/seoUtils";
import { useEffect } from "react";
import { CATEGORY_ICONS } from "@/utils/image/constants";
import { 
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "@/hooks/use-toast";

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = blogPosts.find(post => post.slug === slug);
  const { imageUrl, loading } = useBlogImage(post?.slug || '', post?.category || '');
  
  useEffect(() => {
    if (!post) {
      navigate("/blog");
    }
    // Scroll to top when post changes
    window.scrollTo(0, 0);
  }, [post, navigate]);
  
  if (!post) return null;

  // Get appropriate icon for this category
  const categoryIcon = CATEGORY_ICONS[post.category] || CATEGORY_ICONS['default'];
  
  const postStructuredData = generatePageStructuredData(
    `${post.title} | Hour Tracker Blog`, 
    post.excerpt, 
    `https://www.hourtracker.info/blog/${post.slug}`,
    {
      "@type": "BlogPosting",
      "headline": post.title,
      "image": imageUrl,
      "datePublished": post.publishedAt,
      "author": {
        "@type": "Person",
        "name": post.author.name
      }
    }
  );
  
  const handleCopyLink = () => {
    const url = `${window.location.origin}/blog/${post.slug}`;
    navigator.clipboard.writeText(url).then(
      () => {
        toast({
          title: "Link copied!",
          description: "The post link has been copied to your clipboard",
          duration: 3000,
        });
      },
      (err) => {
        console.error('Could not copy text: ', err);
        toast({
          title: "Failed to copy",
          description: "Please try again or copy the URL manually",
          variant: "destructive",
          duration: 3000,
        });
      }
    );
  };

  const handleShare = async (platform: string) => {
    const url = `${window.location.origin}/blog/${post.slug}`;
    const title = post.title;
    
    try {
      if (navigator.share && platform === 'native') {
        await navigator.share({
          title: title,
          text: post.excerpt,
          url: url
        });
        return;
      }
      
      let shareUrl = '';
      switch (platform) {
        case 'twitter':
          shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`;
          break;
        case 'facebook':
          shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
          break;
        case 'linkedin':
          shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
          break;
        default:
          return;
      }
      
      window.open(shareUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };

  // Check if native sharing is available
  const canUseNativeShare = () => {
    return typeof navigator.share !== 'undefined';
  };
  
  return (
    <div className="min-h-screen flex flex-col">
      <SEO 
        title={`${post.title} | Hour Tracker Blog`} 
        description={post.excerpt} 
        ogImage={imageUrl} 
        ogType="article" 
        keywords={`${post.category}, time tracking, freelancer tips, productivity, ${post.tags.join(", ")}`}
        structuredData={postStructuredData}
      />
      
      <Header />
      
      <main className="flex-1 py-12 bg-gradient-to-b from-gray-50 to-white">
        <div className="container px-4 sm:px-6">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate("/blog")} 
            className="mb-8 hover:bg-transparent hover:text-primary"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to all posts
          </Button>
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-8">
              {/* Blog post content */}
              <article className="bg-card shadow-sm rounded-lg border border-border/40 overflow-hidden">
                <div className="h-[300px] overflow-hidden relative">
                  {loading ? (
                    <div className="w-full h-full bg-muted/30 flex items-center justify-center">
                      <div className="text-primary flex flex-col items-center">
                        {categoryIcon === 'clock' && <Clock size={64} className="mb-4" />}
                        {categoryIcon === 'briefcase' && <Briefcase size={64} className="mb-4" />}
                        {categoryIcon === 'dollar-sign' && <DollarSign size={64} className="mb-4" />}
                        {categoryIcon === 'megaphone' && <Megaphone size={64} className="mb-4" />}
                        {categoryIcon === 'code' && <Code size={64} className="mb-4" />}
                        {categoryIcon === 'palette' && <Palette size={64} className="mb-4" />}
                        {categoryIcon === 'file-text' && <FileText size={64} className="mb-4" />}
                      </div>
                    </div>
                  ) : (
                    <>
                      <img 
                        src={imageUrl} 
                        alt={post.title}
                        className="w-full h-full object-cover"
                        loading="eager"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-black/10" />
                      <div className="absolute bottom-6 left-6 right-6 text-white">
                        <h1 className="text-3xl md:text-4xl font-bold">
                          {post.title}
                        </h1>
                      </div>
                    </>
                  )}
                </div>
                
                <div className="p-8">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                    <span className="bg-primary/10 text-primary px-2 py-1 rounded-md">{post.category}</span>
                    <span>•</span>
                    <span>{new Date(post.publishedAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}</span>
                  </div>
                  
                  <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10 border">
                        <AvatarImage src={post.author.avatar} alt={post.author.name} />
                        <AvatarFallback>{post.author.name.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium">{post.author.name}</p>
                        <p className="text-xs text-muted-foreground">{post.readingTime} min read</p>
                      </div>
                    </div>
                    
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className="gap-2">
                          <Share className="h-4 w-4" />
                          Share
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-56 p-3" align="end">
                        <div className="flex flex-col space-y-1">
                          <p className="text-sm font-medium mb-2">Share this article</p>
                          {canUseNativeShare() && (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="justify-start" 
                              onClick={() => handleShare('native')}
                            >
                              Share via...
                            </Button>
                          )}
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="justify-start" 
                            onClick={() => handleShare('twitter')}
                          >
                            Twitter
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="justify-start" 
                            onClick={() => handleShare('facebook')}
                          >
                            Facebook
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="justify-start" 
                            onClick={() => handleShare('linkedin')}
                          >
                            LinkedIn
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="justify-start" 
                            onClick={handleCopyLink}
                          >
                            Copy link
                          </Button>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                  
                  <div className="prose prose-lg max-w-none" dangerouslySetInnerHTML={{
                  __html: post.content
                }} />
                  
                  <div className="flex flex-wrap gap-2 mt-8">
                    {post.tags.map(tag => <span key={tag} className="bg-muted px-3 py-1 rounded-full text-sm text-muted-foreground">
                        #{tag}
                      </span>)}
                  </div>
                </div>
              </article>
              
              <Separator className="my-10" />
              
              {/* Author bio */}
              <div className="bg-muted/20 border rounded-lg p-6 mb-10">
                <div className="flex items-start gap-4">
                  <Avatar className="h-16 w-16 border">
                    <AvatarImage 
                      src={post.author.avatar || '/placeholder.svg'} 
                      alt={post.author.name}
                    />
                    <AvatarFallback>{post.author.name.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="font-bold text-lg mb-1">About {post.author.name}</h3>
                    <p className="text-sm text-muted-foreground mb-3">{post.author.bio}</p>
                    <Button variant="outline" size="sm">View all posts</Button>
                  </div>
                </div>
              </div>
              
              {/* Related posts */}
              <RelatedPosts currentPostId={post.id} category={post.category} />
            </div>
            
            <div className="lg:col-span-4">
              <BlogSidebar />
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
